import { useAtom, useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { useAnalytics } from '@features/Analytics';
import {
  stripePaymentRetryAtom,
  userAtom,
  userPaymentsInfoAtom,
} from '@features/Stripe/atoms';

type TpRetryPaymentAnalyticsParams = {
  paymentMethod: string;
  planId: string;
};

type TpRetryPaymentErrorEscalationParams = TpRetryPaymentAnalyticsParams & {
  errorCode: string;
  isRetryPaymentAttempt: boolean;
};

const SAME_RETRY_PAYMENT_ERROR_ATTEMPTS_LIMIT = 2;

const getRetryPaymentErrorKey = ({
  errorCode,
  paymentMethod,
  planId,
}: Omit<TpRetryPaymentErrorEscalationParams, 'isRetryPaymentAttempt'>) =>
  [paymentMethod, planId, errorCode].join(':');

export const useStripePaymentRetryAnalytics = () => {
  const [{ retryModalShownCount }, setPaymentRetry] = useAtom(
    stripePaymentRetryAtom,
  );
  const { subscriptionId } = useAtomValue(userPaymentsInfoAtom);
  const { userId } = useAtomValue(userAtom);
  const { trackGoogleEvent } = useAnalytics();

  const getAnalyticsOptions = useCallback(
    ({
      paymentMethod,
      planId,
      retryShownCount,
      retryTappedCount,
    }: TpRetryPaymentAnalyticsParams & {
      retryShownCount: number;
      retryTappedCount: number;
    }) => ({
      payment_method: paymentMethod,
      plan_id: planId,
      retry_modal_shown_count: retryShownCount,
      retry_taped_count: retryTappedCount,
      subscription_id: subscriptionId,
      user_id: userId,
    }),
    [subscriptionId, userId],
  );

  const trackRetryPaymentShown = useCallback(
    ({ paymentMethod, planId }: TpRetryPaymentAnalyticsParams) => {
      let nextRetryModalShownCount = retryModalShownCount + 1;

      setPaymentRetry((previous) => {
        nextRetryModalShownCount = previous.retryModalShownCount + 1;

        return {
          ...previous,
          retryModalShownCount: nextRetryModalShownCount,
        };
      });

      trackGoogleEvent({
        eventName: 'retry-payment-shown',
        options: getAnalyticsOptions({
          paymentMethod,
          planId,
          retryShownCount: nextRetryModalShownCount,
          retryTappedCount: nextRetryModalShownCount,
        }),
      });
    },
    [
      getAnalyticsOptions,
      retryModalShownCount,
      setPaymentRetry,
      trackGoogleEvent,
    ],
  );

  const trackRetryPaymentTapped = useCallback(
    ({ paymentMethod, planId }: TpRetryPaymentAnalyticsParams) => {
      trackGoogleEvent({
        eventName: 'retry-payment-taped',
        options: getAnalyticsOptions({
          paymentMethod,
          planId,
          retryShownCount: retryModalShownCount,
          retryTappedCount: retryModalShownCount,
        }),
      });
    },
    [getAnalyticsOptions, retryModalShownCount, trackGoogleEvent],
  );

  const shouldEscalateRetryPaymentError = useCallback(
    ({
      errorCode,
      isRetryPaymentAttempt,
      paymentMethod,
      planId,
    }: TpRetryPaymentErrorEscalationParams) => {
      let shouldEscalate = false;
      const retryPaymentErrorKey = getRetryPaymentErrorKey({
        errorCode,
        paymentMethod,
        planId,
      });

      setPaymentRetry((previous) => {
        const isSameRetryPaymentError =
          previous.retryPaymentErrorKey === retryPaymentErrorKey;
        const sameRetryablePaymentErrorRetryCount =
          isRetryPaymentAttempt && isSameRetryPaymentError
            ? previous.sameRetryablePaymentErrorRetryCount + 1
            : 0;

        shouldEscalate =
          sameRetryablePaymentErrorRetryCount >=
          SAME_RETRY_PAYMENT_ERROR_ATTEMPTS_LIMIT;

        return {
          ...previous,
          retryPaymentErrorKey,
          sameRetryablePaymentErrorRetryCount,
        };
      });

      return shouldEscalate;
    },
    [setPaymentRetry],
  );

  const resetRetryPaymentErrorEscalation = useCallback(() => {
    setPaymentRetry((previous) => ({
      ...previous,
      retryPaymentErrorKey: '',
      sameRetryablePaymentErrorRetryCount: 0,
    }));
  }, [setPaymentRetry]);

  return {
    resetRetryPaymentErrorEscalation,
    retryModalShownCount,
    shouldEscalateRetryPaymentError,
    trackRetryPaymentShown,
    trackRetryPaymentTapped,
  };
};
